import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, ListGroup, Row, Spinner, Alert } from 'react-bootstrap';
import { validateCertificate } from './utils/certificateValidator';

const FormularioNuevoRNC = ({ onSuccess, onCancel }) => {
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();
    const [guardando, setGuardando] = useState(false);

    // Estados para búsqueda de clientes
    const [clientes, setClientes] = useState([]);
    const [buscandoClientes, setBuscandoClientes] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [terminoBusqueda, setTerminoBusqueda] = useState('');

    const [formData, setFormData] = useState({
        vRNC: '',
        vNombre: '',
        vDireccionFiscal: '',
        vTelefono: '',
        vEmail: '',
        vTipoContribuyente: '',
        vActividadEconomica: '',
        vUsuario: '',
        vPassword: '',
        iAmbiente: '',
        vCertificadoDGII: '',
        vPasswordCertificado: ''
    });

    const [errors, setErrors] = useState({});
    const [certificadoNombre, setCertificadoNombre] = useState('');
    const [certificadoValidacion, setCertificadoValidacion] = useState({
        validado: false,
        esValido: false,
        mensaje: '',
        info: null,
        warnings: []
    });
    const [validandoCertificado, setValidandoCertificado] = useState(false);

    // Timer para el debounce
    const timerBusqueda = useRef(null);

    // Efecto para implementar debounce en la búsqueda
    useEffect(() => {
        // Limpiar el timer anterior
        if (timerBusqueda.current) {
            clearTimeout(timerBusqueda.current);
        }

        // Si el término de búsqueda es muy corto, limpiar resultados
        if (!terminoBusqueda || terminoBusqueda.length < 2) {
            setClientes([]);
            return;
        }

        // Establecer nuevo timer para buscar después de 500ms
        timerBusqueda.current = setTimeout(() => {
            buscarClientes(terminoBusqueda);
        }, 500);

        // Cleanup function
        return () => {
            if (timerBusqueda.current) {
                clearTimeout(timerBusqueda.current);
            }
        };
    }, [terminoBusqueda]);

    // Función para buscar clientes
    const buscarClientes = async (filtro) => {
        if (!filtro || filtro.length < 2) {
            setClientes([]);
            return;
        }

        setBuscandoClientes(true);
        try {
            const response = await fetch(`/api/cliente/GetClientesForDDL?nombre=${encodeURIComponent(filtro)}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data && data.length > 0) {
                    setClientes(data);
                } else {
                    setClientes([]);
                }
            } else {
                showErrorToast('Error al buscar clientes: ' + response.status);
                setClientes([]);
            }
        } catch (error) {
            showErrorToast('Error al buscar clientes: ' + error);
            console.error('Error al buscar clientes:', error);
            setClientes([]);
        } finally {
            setBuscandoClientes(false);
        }
    };

    // Función para seleccionar cliente
    const handleSeleccionarCliente = (cliente) => {
        setClienteSeleccionado(cliente);
        setClientes([]);
        setTerminoBusqueda('');

        // Limpiar error de cliente si existe
        if (errors.cliente) {
            setErrors(prev => {
                const rest = { ...prev };
                delete rest.cliente;
                return rest;
            });
        }
    };

    const ObtenerCadenaRandom = () => {
        let registros = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let cadena = "";
        for (let i = 0; i < 8; i++) {
            cadena += registros.charAt(Math.floor(Math.random() * registros.length));
        }
        return cadena;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Limpiar error del campo cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleUpperCase = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value.toUpperCase()
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // VALIDACIÓN 1: Cliente - REQUERIDO
        if (!clienteSeleccionado || !clienteSeleccionado.idClienteAkasia || clienteSeleccionado.idClienteAkasia <= 0) {
            newErrors.cliente = 'Debe seleccionar un cliente válido para vincular con la facturación';
            showErrorToast('❌ Debe seleccionar un cliente válido antes de continuar');
        }

        // Validar RNC
        if (!formData.vRNC) {
            newErrors.vRNC = 'El RNC es requerido';
        } else if (!/^[0-9]{9,11}$/.test(formData.vRNC)) {
            newErrors.vRNC = 'El RNC debe tener entre 9 y 11 dígitos';
        }

        // Validar Nombre
        if (!formData.vNombre) {
            newErrors.vNombre = 'El Nombre es requerido';
        } else if (formData.vNombre.length > 200) {
            newErrors.vNombre = 'El nombre no debe exceder 200 caracteres';
        }

        // Validar Dirección Fiscal
        if (!formData.vDireccionFiscal) {
            newErrors.vDireccionFiscal = 'La dirección fiscal es requerida';
        } else if (formData.vDireccionFiscal.length < 10) {
            newErrors.vDireccionFiscal = 'La dirección debe tener al menos 10 caracteres';
        } else if (formData.vDireccionFiscal.length > 500) {
            newErrors.vDireccionFiscal = 'La dirección no debe exceder 500 caracteres';
        }

        // Validar Teléfono
        if (!formData.vTelefono) {
            newErrors.vTelefono = 'El teléfono es requerido';
        } else if (!/^[0-9\-+() ]{10,20}$/.test(formData.vTelefono)) {
            newErrors.vTelefono = 'Formato de teléfono inválido';
        }

        // Validar Email
        if (!formData.vEmail) {
            newErrors.vEmail = 'El email es requerido';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.vEmail)) {
            newErrors.vEmail = 'Email inválido';
        }

        // Validar Tipo de Contribuyente
        if (!formData.vTipoContribuyente) {
            newErrors.vTipoContribuyente = 'El tipo de contribuyente es requerido';
        }

        // Validar Actividad Económica
        if (!formData.vActividadEconomica) {
            newErrors.vActividadEconomica = 'La actividad económica es requerida';
        } else if (formData.vActividadEconomica.length > 200) {
            newErrors.vActividadEconomica = 'La actividad económica no debe exceder 200 caracteres';
        }

        // Validar Usuario
        if (!formData.vUsuario) {
            newErrors.vUsuario = 'El usuario es requerido';
        } else if (formData.vUsuario.length > 40) {
            newErrors.vUsuario = 'El usuario no debe exceder 40 caracteres';
        }

        // Validar Contraseña
        if (!formData.vPassword) {
            newErrors.vPassword = 'La contraseña es requerida';
        } else if (formData.vPassword.length > 40) {
            newErrors.vPassword = 'La contraseña no debe exceder 40 caracteres';
        }

        // Validar Ambiente
        if (formData.iAmbiente === '') {
            newErrors.iAmbiente = 'El ambiente es requerido';
        }

        // El token de acceso (vPolarisAuthToken) fue removido del modelo; ya no se valida aquí

        // VALIDACIÓN 2: Certificado Digital - OPCIONAL, pero si se carga la contraseña es obligatoria
        if (formData.vCertificadoDGII) {
            if (!formData.vPasswordCertificado) {
                newErrors.vPasswordCertificado = 'Debe proporcionar la contraseña del certificado';
                showErrorToast('❌ Debe ingresar la contraseña del certificado digital');
            } else if (!certificadoValidacion.validado) {
                newErrors.vPasswordCertificado = 'El certificado aún no ha sido validado. Por favor espere.';
                showErrorToast('⏳ Espere mientras se valida el certificado');
            } else if (!certificadoValidacion.esValido) {
                newErrors.vPasswordCertificado = certificadoValidacion.mensaje || 'La contraseña del certificado es incorrecta';
                showErrorToast('❌ La contraseña del certificado es incorrecta. Por favor verifique.');
            }
        }

        setErrors(newErrors);

        // SOLO retornar true si NO hay errores
        const isValid = Object.keys(newErrors).length === 0;

        if (!isValid) {
            console.log('❌ Validación falló. Errores:', newErrors);
        } else {
            console.log('✅ Validación exitosa. Procediendo a guardar...');
        }

        return isValid;
    };

    const GuardarRNC = async () => {
        setGuardando(true);

        const values = {
            ...formData,
            bIsActive: true,
            idClienteAkasia: clienteSeleccionado?.idClienteAkasia || null
        };

        console.log('Datos a enviar:', values);

        try {
            const response = await fetch("/api/FacturaRepublicaDominicana/GuardarRNC", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(values)
            });

            if (response.ok) {
                const data = await response.json();
                console.log(data);

                if (data.iResponseType == 1) {
                    handleReset();
                    onSuccess();
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
                console.log(response);
                if (response.status == 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
            console.log('Hubo un problema con la peticion Fetch:' + error);
        } finally {
            setGuardando(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            GuardarRNC();
        }
    };

    const handleReset = () => {
        setFormData({
            vRNC: '',
            vNombre: '',
            vDireccionFiscal: '',
            vTelefono: '',
            vEmail: '',
            vTipoContribuyente: '',
            vActividadEconomica: '',
            vUsuario: '',
            vPassword: '',
            iAmbiente: '',
            vCertificadoDGII: '',
            vPasswordCertificado: ''
        });
        setErrors({});
        setClienteSeleccionado(null);
        setClientes([]);
        setTerminoBusqueda('');
        setCertificadoNombre('');
        setCertificadoValidacion({
            validado: false,
            esValido: false,
            mensaje: '',
            info: null,
            warnings: []
        });
    };

    const handleCancel = () => {
        handleReset();
        if (onCancel) onCancel();
    };

    const handleGenerarUsuario = () => {
        const cadena = ObtenerCadenaRandom();
        setFormData(prev => ({
            ...prev,
            vUsuario: cadena
        }));
    };

    const handleGenerarPassword = () => {
        const cadena = ObtenerCadenaRandom();
        setFormData(prev => ({
            ...prev,
            vPassword: cadena
        }));
    };

    const handleCertificadoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar que sea un archivo .pfx o .p12
            const extension = file.name.toLowerCase().split('.').pop();
            if (extension !== 'pfx' && extension !== 'p12') {
                alert('Por favor seleccione un archivo de certificado válido (.pfx o .p12)');
                event.target.value = '';
                return;
            }

            // Convertir el archivo a base64
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64 = e.target.result.split(',')[1]; // Obtener solo la parte base64
                setFormData(prev => ({
                    ...prev,
                    vCertificadoDGII: base64
                }));
                setCertificadoNombre(file.name);

                // Reset validación
                setCertificadoValidacion({
                    validado: false,
                    esValido: false,
                    mensaje: '',
                    info: null,
                    warnings: []
                });

                // Si ya hay contraseña, validar automáticamente
                if (formData.vPasswordCertificado) {
                    validarCertificadoConPassword(base64, formData.vPasswordCertificado);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const validarCertificadoConPassword = async (base64Cert, password) => {
        if (!base64Cert || !password) return;

        setValidandoCertificado(true);

        // Pequeño delay para dar feedback visual
        setTimeout(() => {
            try {
                const resultado = validateCertificate(base64Cert, password);

                setCertificadoValidacion({
                    validado: true,
                    esValido: resultado.isValid,
                    mensaje: resultado.error || 'Certificado válido',
                    info: resultado.certificateInfo,
                    warnings: resultado.warnings || []
                });

                if (!resultado.isValid) {
                    setErrors(prev => ({
                        ...prev,
                        vPasswordCertificado: resultado.error
                    }));
                } else {
                    // Limpiar error si es válido
                    setErrors(prev => {
                        const rest = { ...prev };
                        delete rest.vPasswordCertificado;
                        return rest;
                    });
                }
            } catch (error) {
                console.error('Error validando certificado:', error);
                setCertificadoValidacion({
                    validado: true,
                    esValido: false,
                    mensaje: 'Error al validar el certificado',
                    info: null,
                    warnings: []
                });
            } finally {
                setValidandoCertificado(false);
            }
        }, 300);
    };

    // Efecto para validar cuando cambie la contraseña
    useEffect(() => {
        if (formData.vCertificadoDGII && formData.vPasswordCertificado) {
            const timer = setTimeout(() => {
                validarCertificadoConPassword(formData.vCertificadoDGII, formData.vPasswordCertificado);
            }, 500); // Debounce de 500ms

            return () => clearTimeout(timer);
        } else {
            setCertificadoValidacion({
                validado: false,
                esValido: false,
                mensaje: '',
                info: null,
                warnings: []
            });
        }
    }, [formData.vPasswordCertificado, formData.vCertificadoDGII]);

    return (
        <Form noValidate onSubmit={handleSubmit}>
            <div className="mb-4">
                <p className="text-muted small mb-0">
                    Complete la información del Registro Nacional del Contribuyente según lineamientos de la DGII.
                </p>
            </div>

            {/* Sección de búsqueda de cliente */}
            <Card className={`mb-4 ${errors.cliente ? 'border-danger' : 'border-primary'}`}>
                <Card.Header className="bg-light">
                    <h6 className="mb-0 fw-semibold small">
                        <FontAwesomeIcon icon="building" className="me-2 text-primary" />
                        Vincular Cliente *
                        {errors.cliente && (
                            <span className="text-danger ms-2">
                                <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
                                Requerido
                            </span>
                        )}
                    </h6>
                </Card.Header>
                <Card.Body>
                    {!clienteSeleccionado ? (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small">Buscar Cliente</Form.Label>
                                <Form.Control
                                    size="sm"
                                    placeholder="Escriba el nombre o ID del cliente (mínimo 2 caracteres)"
                                    value={terminoBusqueda}
                                    onChange={(e) => setTerminoBusqueda(e.target.value)}
                                    isInvalid={!!errors.cliente}
                                />
                                {errors.cliente && (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                        {errors.cliente}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted">
                                    Busque al cliente para vincular con la facturación
                                </Form.Text>
                            </Form.Group>

                            {buscandoClientes && (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                    <span className="ms-2">Buscando clientes...</span>
                                </div>
                            )}

                            {clientes.length > 0 && (
                                <ListGroup>
                                    {clientes.map((cliente) => (
                                        <ListGroup.Item
                                            key={cliente.idClienteAkasia}
                                            action
                                            onClick={() => handleSeleccionarCliente(cliente)}
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <strong>{cliente.vNombre} {cliente.vApellidos}</strong>
                                                <br />
                                                <small className="text-muted">
                                                    ID: {cliente.idClienteAkasia} | {cliente.vPais} - {cliente.vEstadoDepProv}
                                                </small>
                                            </div>
                                            <Button variant="primary" size="sm">
                                                <FontAwesomeIcon icon="check" />
                                            </Button>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </>
                    ) : (
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mb-1">
                                    <FontAwesomeIcon icon="user" className="me-2 text-success" />
                                    {clienteSeleccionado.vNombre} {clienteSeleccionado.vApellidos}
                                </h6>
                                <small className="text-muted">
                                    ID: {clienteSeleccionado.idClienteAkasia} | {clienteSeleccionado.vPais} - {clienteSeleccionado.vEstadoDepProv}
                                </small>
                            </div>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setClienteSeleccionado(null)}
                            >
                                <FontAwesomeIcon icon="times" className="me-1" />
                                Cambiar
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* RNC y Nombre en el mismo renglón */}
            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">RNC *</Form.Label>
                        <Form.Control
                            size="sm"
                            name="vRNC"
                            value={formData.vRNC}
                            onChange={handleChange}
                            isInvalid={!!errors.vRNC}
                            placeholder="123456789"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vRNC}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={8}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Razón Social / Nombre Comercial *</Form.Label>
                        <Form.Control
                            size="sm"
                            name="vNombre"
                            value={formData.vNombre}
                            onChange={handleChange}
                            onKeyUp={handleUpperCase}
                            isInvalid={!!errors.vNombre}
                            placeholder="Nombre del contribuyente"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vNombre}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Dirección Fiscal *</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    name="vDireccionFiscal"
                    value={formData.vDireccionFiscal}
                    onChange={handleChange}
                    onKeyUp={handleUpperCase}
                    isInvalid={!!errors.vDireccionFiscal}
                    placeholder="Dirección completa del contribuyente"
                />
                <Form.Control.Feedback type="invalid">
                    {errors.vDireccionFiscal}
                </Form.Control.Feedback>
            </Form.Group>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Teléfono *</Form.Label>
                        <Form.Control
                            size="sm"
                            name="vTelefono"
                            value={formData.vTelefono}
                            onChange={handleChange}
                            isInvalid={!!errors.vTelefono}
                            placeholder="809-123-4567"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vTelefono}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Email de Facturación *</Form.Label>
                        <Form.Control
                            size="sm"
                            type="email"
                            name="vEmail"
                            value={formData.vEmail}
                            onChange={handleChange}
                            isInvalid={!!errors.vEmail}
                            placeholder="correo@empresa.com"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vEmail}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Tipo de Contribuyente *</Form.Label>
                        <Form.Select
                            size="sm"
                            name="vTipoContribuyente"
                            value={formData.vTipoContribuyente}
                            onChange={handleChange}
                            isInvalid={!!errors.vTipoContribuyente}
                        >
                            <option value="">Seleccione...</option>
                            <option value="PERSONA_FISICA">Persona Física</option>
                            <option value="PERSONA_JURIDICA">Persona Jurídica</option>
                            <option value="REGIMEN_SIMPLIFICADO">Régimen Simplificado</option>
                            <option value="GRAN_CONTRIBUYENTE">Gran Contribuyente</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.vTipoContribuyente}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Actividad Económica *</Form.Label>
                        <Form.Control
                            size="sm"
                            name="vActividadEconomica"
                            value={formData.vActividadEconomica}
                            onChange={handleChange}
                            onKeyUp={handleUpperCase}
                            isInvalid={!!errors.vActividadEconomica}
                            placeholder="Ej: COMERCIO AL POR MENOR"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vActividadEconomica}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <hr className="my-3" />

            <h6 className="mb-3 fw-semibold small">
                <FontAwesomeIcon icon="cog" className="me-2 text-primary" />
                Configuración de Ambiente
            </h6>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Ambiente *</Form.Label>
                        <Form.Select
                            size="sm"
                            name="iAmbiente"
                            value={formData.iAmbiente}
                            onChange={handleChange}
                            isInvalid={!!errors.iAmbiente}
                        >
                            <option value="">Seleccione...</option>
                            {/*<option value="0">Desarrollo</option>*/}
                            <option value="1">Test</option>
                            <option value="2">Certificación</option>
                            <option value="3">Producción</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.iAmbiente}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                    </Form.Group>
                </Col>
            </Row>

            <hr className="my-3" />

            <h6 className="mb-3 fw-semibold small">
                <FontAwesomeIcon icon="certificate" className="me-2 text-primary" />
                Certificado Digital
            </h6>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Archivo del Certificado (.pfx / .p12)</Form.Label>
                <Form.Control
                    size="sm"
                    type="file"
                    accept=".pfx,.p12"
                    onChange={handleCertificadoChange}
                    isInvalid={!!errors.vCertificadoDGII}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.vCertificadoDGII}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                    {certificadoNombre ? (
                        <>
                            <FontAwesomeIcon icon="check-circle" className="text-success me-1" />
                            {certificadoNombre}
                        </>
                    ) : (
                        'Opcional: Seleccione un archivo de certificado digital (.pfx o .p12)'
                    )}
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Contraseña del Certificado *</Form.Label>
                <Form.Control
                    size="sm"
                    type="password"
                    name="vPasswordCertificado"
                    value={formData.vPasswordCertificado}
                    onChange={handleChange}
                    isInvalid={!!errors.vPasswordCertificado || (certificadoValidacion.validado && !certificadoValidacion.esValido && formData.vCertificadoDGII)}
                    isValid={certificadoValidacion.validado && certificadoValidacion.esValido && formData.vCertificadoDGII}
                    placeholder="Contraseña para desbloquear el certificado"
                    disabled={!formData.vCertificadoDGII}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.vPasswordCertificado || certificadoValidacion.mensaje}
                </Form.Control.Feedback>
                <Form.Control.Feedback type="valid">
                    <FontAwesomeIcon icon="check-circle" className="me-1" />
                    Contraseña correcta - Certificado válido
                </Form.Control.Feedback>
                {validandoCertificado && (
                    <Form.Text className="text-info d-block mt-1">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Validando certificado...
                    </Form.Text>
                )}
                {!validandoCertificado && formData.vCertificadoDGII && !formData.vPasswordCertificado && (
                    <Form.Text className="text-muted d-block mt-1">
                        Ingrese la contraseña para validar el certificado
                    </Form.Text>
                )}
            </Form.Group>

            {/* Información del Certificado */}
            {certificadoValidacion.validado && certificadoValidacion.esValido && certificadoValidacion.info && (
                <Alert variant="success" className="mb-3">
                    <Alert.Heading className="h6">
                        <FontAwesomeIcon icon="certificate" className="me-2" />
                        Información del Certificado
                    </Alert.Heading>
                    <hr />
                    <Row>
                        <Col md={6}>
                            <small className="text-muted">Nombre Común (CN)</small>
                            <div className="fw-semibold">{certificadoValidacion.info.commonName}</div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">Organización</small>
                            <div className="fw-semibold">{certificadoValidacion.info.organization}</div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={6}>
                            <small className="text-muted">Válido desde</small>
                            <div className="fw-semibold">{certificadoValidacion.info.validFrom}</div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">Válido hasta</small>
                            <div className="fw-semibold">{certificadoValidacion.info.validTo}</div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={12}>
                            <small className="text-muted">Emisor</small>
                            <div className="fw-semibold">{certificadoValidacion.info.issuer}</div>
                        </Col>
                    </Row>

                    {/* Advertencias */}
                    {certificadoValidacion.warnings && certificadoValidacion.warnings.length > 0 && (
                        <div className="mt-3">
                            {certificadoValidacion.warnings.map((warning, idx) => (
                                <div key={idx} className="text-warning">
                                    <small>{warning}</small>
                                </div>
                            ))}
                        </div>
                    )}
                </Alert>
            )}

            <hr className="my-3" />

            <h6 className="mb-3 fw-semibold small">
                <FontAwesomeIcon icon="key" className="me-2 text-primary" />
                Credenciales de Acceso
            </h6>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">
                            Usuario
                            <Button
                                variant="link"
                                size="sm"
                                className="ms-2 p-0 text-decoration-none"
                                onClick={handleGenerarUsuario}
                                type="button"
                            >
                                <FontAwesomeIcon icon="sync-alt" className="me-1" />
                                Generar
                            </Button>
                        </Form.Label>
                        <Form.Control
                            size="sm"
                            name="vUsuario"
                            value={formData.vUsuario}
                            onChange={handleChange}
                            onKeyUp={handleUpperCase}
                            isInvalid={!!errors.vUsuario}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vUsuario}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">
                            Contraseña
                            <Button
                                variant="link"
                                size="sm"
                                className="ms-2 p-0 text-decoration-none"
                                onClick={handleGenerarPassword}
                                type="button"
                            >
                                <FontAwesomeIcon icon="sync-alt" className="me-1" />
                                Generar
                            </Button>
                        </Form.Label>
                        <Form.Control
                            size="sm"
                            name="vPassword"
                            value={formData.vPassword}
                            onChange={handleChange}
                            onKeyUp={handleUpperCase}
                            isInvalid={!!errors.vPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.vPassword}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <div className="d-grid gap-2 mt-4">
                <Button
                    type="submit"
                    variant="success"
                    disabled={guardando}
                    size="sm"
                >
                    {guardando ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon="save" className="me-2" />
                            Registrar RNC
                        </>
                    )}
                </Button>
                <Button
                    variant="outline-secondary"
                    onClick={handleCancel}
                    disabled={guardando}
                    size="sm"
                >
                    <FontAwesomeIcon icon="times" className="me-2" />
                    Cancelar
                </Button>
            </div>
        </Form>
    );
};

FormularioNuevoRNC.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func
};

export default FormularioNuevoRNC;
